import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import type { Citation, MediaBlock as MediaBlockModel, VideoChapter } from "@shared/types";
import type { BlockRendererProps } from "./registry";

/**
 * Media-block renderer (FEATURE_IA_MEDIA): a cited demo video / doc / case
 * rendered inside the structured answer, served by the host (citation-driven,
 * no-download). Chapter seek waits for metadata on a cold `preload="none"`
 * player; the doc pager / stepshow reset when the block changes.
 *
 * Collapsed by default: media surfaces as a compact one-line OFFER (▶ kind ·
 * title · 点开查看), so a relevant demo is DISCOVERABLE without dumping a wall of
 * video/slides/steps into a how-to or location answer. The heavy body is mounted
 * only on expand (no video preload, no stepshow autoplay until the user opts in).
 */
export function MediaBlockView({ block, citations }: BlockRendererProps): JSX.Element {
  const media = block as MediaBlockModel;
  const [open, setOpen] = useState(false);
  if (!open) return <MediaTeaser media={media} onOpen={() => setOpen(true)} />;
  const body =
    media.kind === "doc" ? (
      <DocMedia media={media} />
    ) : media.kind === "stepshow" ? (
      <StepshowMedia media={media} />
    ) : (
      <VideoMedia media={media} citations={citations} />
    );
  return (
    <div className="media-open">
      {body}
      <button type="button" className="media-collapse" onClick={() => setOpen(false)}>
        收起
      </button>
    </div>
  );
}

/** Kind label + glyph for the collapsed teaser. */
function mediaKindMeta(media: MediaBlockModel): { label: string; glyph: JSX.Element } {
  if (media.kind === "doc") return { label: "资料", glyph: <DocGlyph /> };
  if (media.kind === "stepshow") return { label: "操作演示", glyph: <VideoGlyph /> };
  return { label: "演示视频", glyph: <VideoGlyph /> };
}

/** Compact, clickable one-line offer — the default (collapsed) state of a media block. */
function MediaTeaser({ media, onOpen }: { media: MediaBlockModel; onOpen: () => void }): JSX.Element {
  const { label, glyph } = mediaKindMeta(media);
  const extra = media.kind === "stepshow" && media.steps !== undefined ? ` · ${media.steps.length} 步` : "";
  return (
    <button type="button" className="media-block media-teaser" onClick={onOpen}>
      <span className="media-teaser-kind">
        {glyph}
        {label}
      </span>
      <span className="media-teaser-title">
        {media.title}
        {extra}
      </span>
      <span className="media-teaser-cue" aria-hidden="true">
        点开查看 →
      </span>
    </button>
  );
}

/** Document glyph for the doc-kind teaser. */
function DocGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"
      aria-hidden="true">
      <path d="M6 2h9l5 5v15H6z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

/** Play-triangle glyph for the video header (single accent, no decorative emoji). */
function VideoGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"
      aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M10 9l5 3-5 3z" fill="currentColor" />
    </svg>
  );
}

/** Seekable self-hosted video with chapters. */
function VideoMedia({ media, citations }: { media: MediaBlockModel; citations: Citation[] }): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const src = `/api/video/${media.token}`;
  const poster = `/api/video/${media.token}/poster.jpg`;
  // Chapters: prefer the model-carried ones, else borrow from the cited source's
  // surfaced chapters (search_kb computes them from the transcript by videoToken),
  // so the player keeps chapter nav even when the block only passed a token.
  const chapters: VideoChapter[] =
    media.chapters !== undefined && media.chapters.length > 0
      ? media.chapters
      : (citations.find((c) => c.videoToken === media.token)?.chapters ?? []);

  /**
   * Seek to a chapter. On a cold `preload="none"` player metadata isn't loaded
   * yet, so we (1) always call play() — which triggers the source load — and
   * (2) apply the seek now if metadata is ready, else once `loadedmetadata`
   * fires. Calling play() is what makes the load happen; waiting for metadata
   * without it would leave a fresh player stuck.
   */
  const seekTo = (chapter: VideoChapter): void => {
    const v = videoRef.current;
    if (v === null) return;
    const applySeek = (): void => {
      v.currentTime = chapter.secondsOffset;
    };
    if (v.readyState >= 1) applySeek();
    else v.addEventListener("loadedmetadata", applySeek, { once: true });
    void v.play().catch(() => undefined);
  };

  return (
    <div className="media-block media-video">
      <div className="media-head media-head-label">
        <VideoGlyph />
        <span>演示视频</span>
      </div>
      <video
        ref={videoRef}
        className="media-video-player"
        src={src}
        poster={poster}
        controls
        preload="none"
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
      />
      <div className="media-video-title">{media.title}</div>
      {chapters.length > 0 && (
        <div className="media-chapters-gallery">
          <div className="media-chapters-head">{`在此视频中，有 ${chapters.length} 个重要时刻`}</div>
          <ol className="media-chapter-row">
            {chapters.map((c, i) => (
              <li key={`${c.secondsOffset}-${i}`}>
                <button type="button" className="media-chapter-card" onClick={() => seekTo(c)} title="跳到该时间点">
                  <span className="media-chapter-thumb">
                    <img
                      src={`/api/video/${media.token}/frame/${Math.floor(c.secondsOffset)}.jpg`}
                      alt=""
                      loading="lazy"
                      draggable={false}
                    />
                    <span className="media-chapter-badge">{formatTime(c.secondsOffset)}</span>
                  </span>
                  <span className="media-chapter-cap">{c.label.length > 0 ? c.label : "（无字幕）"}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}
      {media.sourceUrl !== undefined && media.sourceUrl.length > 0 && (
        <a className="media-source" href={media.sourceUrl} target="_blank" rel="noreferrer">
          打开原始来源
        </a>
      )}
    </div>
  );
}

/** Embedded slides/PDF as page images, paginated. */
function DocMedia({ media }: { media: MediaBlockModel }): JSX.Element {
  const [page, setPage] = useState(1);
  // Reset the pager when the block (asset) changes, so the index never points
  // past a different doc's pages.
  useEffect(() => setPage(1), [media.id]);
  const pages = media.pages !== undefined && media.pages > 0 ? media.pages : 1;
  const clamped = Math.min(Math.max(page, 1), pages);
  return (
    <div className="media-block media-doc">
      <div className="media-head">{media.title}</div>
      <img
        className="media-doc-page"
        src={`/api/asset/doc/${media.assetId}/page/${clamped}.png`}
        alt={`${media.title} 第 ${clamped} 页`}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
      />
      {pages > 1 && (
        <div className="media-doc-nav">
          <button type="button" disabled={clamped <= 1} onClick={() => setPage(clamped - 1)}>
            上一页
          </button>
          <span className="media-doc-count">
            {clamped} / {pages}
          </span>
          <button type="button" disabled={clamped >= pages} onClick={() => setPage(clamped + 1)}>
            下一页
          </button>
        </div>
      )}
      {media.sourceUrl !== undefined && media.sourceUrl.length > 0 && (
        <a className="media-source" href={media.sourceUrl} target="_blank" rel="noreferrer">
          打开原始来源
        </a>
      )}
    </div>
  );
}

/** Per-slide dwell while a stepshow auto-plays (ms). */
const STEPSHOW_SLIDE_MS = 3200;

/**
 * Screenshot walkthrough — a solution case played as a pseudo-interactive video:
 * the case's screenshots auto-advance, stopping at the end and on any manual nav.
 * A play/pause toggle resumes it.
 */
function StepshowMedia({ media }: { media: MediaBlockModel }): JSX.Element {
  const steps = media.steps ?? [];
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(steps.length > 1);
  // Reset to the first slide + resume autoplay when the block (case) changes.
  useEffect(() => {
    setI(0);
    setPlaying(steps.length > 1);
  }, [media.id, steps.length]);
  // Auto-advance while playing; stop at the last slide.
  useEffect(() => {
    if (!playing) return undefined;
    const t = window.setTimeout(() => {
      setI((cur) => {
        if (cur + 1 >= steps.length) {
          setPlaying(false);
          return cur;
        }
        return cur + 1;
      });
    }, STEPSHOW_SLIDE_MS);
    return () => window.clearTimeout(t);
  }, [playing, i, steps.length]);

  const idx = Math.min(Math.max(i, 0), Math.max(steps.length - 1, 0));
  const step = steps[idx];
  // Manual nav stops autoplay so the user can read at their own pace.
  const go = (next: number): void => {
    setPlaying(false);
    setI(Math.max(0, Math.min(steps.length - 1, next)));
  };
  const atEnd = idx >= steps.length - 1;

  return (
    <div className="media-block media-stepshow">
      <div className="media-head media-head-label">
        <VideoGlyph />
        <span>
          操作演示 · {idx + 1}/{steps.length}
        </span>
      </div>
      {step !== undefined && (
        <>
          <img
            className="media-step-img"
            src={step.image}
            alt={step.caption}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
          {step.caption.length > 0 && <div className="media-step-caption">{step.caption}</div>}
        </>
      )}
      {steps.length > 1 && (
        <div className="media-doc-nav">
          <button type="button" disabled={idx <= 0} onClick={() => go(idx - 1)}>
            上一步
          </button>
          <button
            type="button"
            className="media-step-play"
            onClick={() => (atEnd ? (setI(0), setPlaying(true)) : setPlaying((p) => !p))}
          >
            {atEnd ? "重播" : playing ? "暂停" : "播放"}
          </button>
          <span className="media-doc-count">
            {idx + 1} / {steps.length}
          </span>
          <button type="button" disabled={atEnd} onClick={() => go(idx + 1)}>
            下一步
          </button>
        </div>
      )}
    </div>
  );
}

/** Seconds -> mm:ss (or h:mm:ss). */
function formatTime(total: number): string {
  const s = Math.max(0, Math.floor(total));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
